import * as React from 'react';
import {useState} from 'react';
import {Box, IconButton, Tab, Tabs, Tooltip} from '@mui/material';
import {ChevronLeft, ChevronRight} from '@mui/icons-material';

export interface CollapsibleSidebarTab {
    id: string;
    label: string;
    // ReactElement (not ReactNode): MUI's Tab icon prop requires an element or string
    icon: React.ReactElement;
    content: React.ReactNode;
}

export interface CollapsibleSidebarProps {
    tabs: CollapsibleSidebarTab[];
    expandedMinWidth?: number;
    expandedMaxWidth?: number;
    collapsedWidth?: number;
    initialTabId?: string;
    // Called when the sidebar transitions from expanded to collapsed, e.g. to
    // close a surrounding drawer so the main content takes over the space.
    onCollapse?: () => void;
}

const DEFAULT_EXPANDED_MIN_WIDTH = 200;
const DEFAULT_EXPANDED_MAX_WIDTH = 400;
const DEFAULT_COLLAPSED_WIDTH = 48;

/**
 * Collapsible left sidebar with tab switching. When expanded it shows a collapse
 * chevron plus one tab per entry; when collapsed it shrinks to an icon rail where
 * each tab's icon expands the sidebar directly onto that tab.
 */
export const CollapsibleSidebar: React.FC<CollapsibleSidebarProps> = (props) => {
    const {
        tabs,
        expandedMinWidth = DEFAULT_EXPANDED_MIN_WIDTH,
        expandedMaxWidth = DEFAULT_EXPANDED_MAX_WIDTH,
        collapsedWidth = DEFAULT_COLLAPSED_WIDTH,
        initialTabId,
        onCollapse,
    } = props;
    const [collapsed, setCollapsed] = useState(false);
    const [activeTabId, setActiveTabId] = useState<string>(initialTabId ?? tabs[0]?.id ?? '');

    if (tabs.length === 0) {
        return null;
    }

    const activeTab = tabs.find(tab => tab.id === activeTabId) ?? tabs[0];

    const expandToTab = (tabId: string) => {
        setActiveTabId(tabId);
        setCollapsed(false);
    };

    return (
        <Box
            sx={{
                minWidth: collapsed ? collapsedWidth : expandedMinWidth,
                maxWidth: collapsed ? collapsedWidth : expandedMaxWidth,
                flexShrink: 0,
                transition: 'min-width 0.2s, max-width 0.2s',
                borderColor: 'divider',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mt: 2,
                    mb: 1,
                }}
            >
                <IconButton
                    size="small"
                    onClick={() => {
                        const nextCollapsed = !collapsed;
                        setCollapsed(nextCollapsed);
                        if (nextCollapsed) onCollapse?.();
                    }}
                    aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                >
                    {collapsed ? <ChevronRight/> : <ChevronLeft/>}
                </IconButton>

                {!collapsed && tabs.length > 1 && (
                    <Tabs
                        value={activeTab.id}
                        onChange={(event, value) => setActiveTabId(value as string)}
                        sx={{ml: 1, minHeight: 36, flexGrow: 1}}
                    >
                        {tabs.map(tab => (
                            <Tab
                                key={tab.id}
                                value={tab.id}
                                icon={tab.icon}
                                iconPosition="start"
                                label={tab.label}
                                sx={{minHeight: 36}}
                            />
                        ))}
                    </Tabs>
                )}
            </Box>

            {collapsed ? (
                <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, mt: 1}}>
                    {tabs.map(tab => (
                        <Tooltip key={tab.id} title={tab.label}>
                            <IconButton
                                size="small"
                                onClick={() => expandToTab(tab.id)}
                                aria-label={tab.label}
                            >
                                {tab.icon}
                            </IconButton>
                        </Tooltip>
                    ))}
                </Box>
            ) : (
                <Box sx={{flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column'}}>
                    {activeTab.content}
                </Box>
            )}
        </Box>
    );
};